import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { AiOutlineClose } from 'react-icons/ai';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../../../enums/queryKeys';
import PermissionManagerService from '../../../../../services/FileManager/permissionManagement.service';
import { PermissionsPicker, splitPermissionValues, toPermissionValues } from '../../../../../shared/components/PermissionsPicker';
import { usePermissionPrincipals } from '../../../../../hooks/usePermissionPrincipals';
import ConditionalRender from '../../../../../shared/components/ConditionalRender';
import { PermissionResourceType, PermissionsPickerVariant } from '../../../../../enums/permissionEnums';
import type { FileNode } from '../../../../../types/FileManagerTypes';
import { generateFilePath, type FileLocationMap } from '../FileDetailsModal';
import './FilePermissionsModal.scss';

interface FilePermissionsModalProps {
  open: boolean;
  onClose: () => void;
  file?: FileNode;
  retrieveFileLocation: (fileId: string) => Promise<{ fileMap: FileLocationMap; parentFolderId: string } | undefined>;
}

interface ExistingPermissions {
  userPermissions?: unknown;
  groupPermissions?: unknown;
}

const toIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .map((entry) => (typeof entry === 'object' && entry !== null && 'id' in entry ? (entry as { id: unknown }).id : entry))
        .filter((id): id is string | number => id !== undefined && id !== null)
        .map(String)
    : [];

export const FilePermissionsModal: React.FC<FilePermissionsModalProps> = ({ open, onClose, file, retrieveFileLocation }) => {
  const resourceId = file?.id;
  const resourceType = file?.isDir ? PermissionResourceType.FOLDER : PermissionResourceType.FILE;

  const queryClient = useQueryClient();
  const { getPermissions, updatePermissions } = PermissionManagerService();
  const { users, groups, isLoading } = usePermissionPrincipals(open);
  // staleTime 0 (against the app-wide 30s default): permissions can change elsewhere, so every open refetches.
  const { data: existing, isFetching: isFetchingExisting } = useQuery({ ...getPermissions(resourceId), enabled: open && !!resourceId, staleTime: 0 });
  const { mutateAsync: updatePermissionsAsync } = useMutation(updatePermissions());

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const hasSeededRef = useRef(false);

  const [location, setLocation] = useState('');

  // Clear on close so an abandoned edit / stale location can't leak into the next item.
  useEffect(() => {
    if (open) return;
    hasSeededRef.current = false;
    setSelectedValues([]);
    setLocation('');
  }, [open]);

  // Seed once the fresh fetch settles (not from a cached copy), and only once -- a
  // refetch resolving mid-edit would otherwise discard whatever the user has ticked.
  useEffect(() => {
    if (!open || hasSeededRef.current || existing === undefined || isFetchingExisting) return;
    hasSeededRef.current = true;
    const permissions = existing as ExistingPermissions;
    setSelectedValues(toPermissionValues(toIds(permissions.userPermissions), toIds(permissions.groupPermissions)));
  }, [open, existing, isFetchingExisting]);

  // Breadcrumb display only -- not sent to the backend, which only needs resourceId/resourceType.
  useEffect(() => {
    if (!open || !resourceId) return;
    let cancelled = false;
    retrieveFileLocation(resourceId).then((data) => {
      if (!cancelled) setLocation(data ? generateFilePath(data.fileMap, data.parentFolderId) : '');
    });
    return () => {
      cancelled = true;
    };
  }, [open, resourceId, retrieveFileLocation]);

  const handleSet = async () => {
    if (!resourceId) return;
    const { userValues, groupValues } = splitPermissionValues(selectedValues);
    setIsSaving(true);
    try {
      await updatePermissionsAsync({ userValues, groupValues, resourceId, resourceType });
      // The global 30s staleTime would otherwise re-seed the next open from
      // the pre-save response.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PERMISSIONS, resourceId] });
      onClose();
    } catch {
      // updatePermissions already toasts via its onError; keep the modal open
      // with the selection intact so the user can retry.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered closable={false} width={520} className="file-permissions-modal">
      <div className="file-permissions-modal__header">
        <div className="file-permissions-modal__heading">
          <span className="file-permissions-modal__title">Permissions</span>
          <ConditionalRender visible={!!file?.name} hideFallback>
            <span className="file-permissions-modal__name">{file?.name}</span>
          </ConditionalRender>
        </div>
        <button type="button" onClick={onClose} className="file-permissions-modal__close">
          <AiOutlineClose />
        </button>
      </div>

      <ConditionalRender visible={!!location} hideFallback>
        <div className="file-permissions-modal__location">{location}</div>
      </ConditionalRender>

      <div className="file-permissions-modal__picker">
        <PermissionsPicker
          users={users}
          groups={groups}
          selectedValues={selectedValues}
          onChange={setSelectedValues}
          isLoading={isLoading || isFetchingExisting}
          variant={PermissionsPickerVariant.DARK}
        />
      </div>

      <div className="file-permissions-modal__actions">
        <Button className="file-permissions-modal__set-btn" onClick={handleSet} loading={isSaving} disabled={isSaving || !resourceId}>
          Set
        </Button>
      </div>
    </Modal>
  );
};

export default FilePermissionsModal;
